import React, { useState, useRef, useContext } from "react";
import { Button, Input } from "antd";
import CommonModal from "./common/modal";
import { genContentByAttrs } from "./common/text";
import { isZH } from "./locale";
import { ToolFnContext } from "./utils/context";
import { getDefaultComponentStyle } from "./utils/defaultStyles";
import debounce from "lodash/debounce";

const { TextArea } = Input;

// 0:大 1:中 2:小，对应按钮高度如下
const btnFrameMap = [32, 26, 20];

export default ({
  data,
  getText,
  btnRequestHandle,
  isStreaming,
}: {
  data: IMultiElementCombineMsg.IButtonElem;
  getText: (text: IMultiElementCombineMsg.IText) => string;
  btnRequestHandle: ({
    url,
    btnId,
    retrieve_values,
    extra,
    success_toast,
    error_toast,
  }: IMultiElementCombineMsg.ISubmitParams) => void;
  isStreaming: boolean;
}) => {
  const { content, frame_style = 0 } = data;
  // const [panelInfo, setGroupPanelInfo] = useState<any>(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [modalInfo, setModalInfo] = useState<any>({ title: "", content: "" });
  const [confirmInputValue, setConfirmInputValue] = useState("");

  const btnItemInfo = useRef<IMultiElementCombineMsg.IBtnItem>();
  const isConfirmInputComposingRef = useRef(false);

  const { btnExtraHandle, track, parentBoxName } = useContext(ToolFnContext);

  const style = getDefaultComponentStyle(data);

  const btnStyle = (btnItem: IMultiElementCombineMsg.IBtnItem) => {
    const { text_color, border_color, background_color } = btnItem;

    const heightValue = `${btnFrameMap[frame_style]}px`;
    return {
      color: `#${text_color}`,
      borderColor: `#${border_color}`,
      backgroundColor: `#${background_color}`,
      height: heightValue,
      lineHeight: heightValue,
    };
  };

  const btnClick = debounce(
    (e: any, item: IMultiElementCombineMsg.IBtnItem) => {
      console.log("bgm-button", e, item);
      e && e.stopPropagation();
      const clientY = e ? e.clientY : 0;
      btnItemInfo.current = item;

      const { confirm } = item;
      let content: any, title: string | undefined;
      let showInput: boolean | undefined;
      if (confirm) {
        title = getText(confirm.title);
        const { attributes_i18n } = confirm.content;
        const attrs = isZH() ? attributes_i18n?.zh_cn : attributes_i18n?.en_us;
        content = attrs ? genContentByAttrs(attrs) : getText(confirm.content);
        showInput = confirm.show_input;
      }

      if (title || content || showInput) {
        setConfirmInputValue("");
        setConfirmModal(true);
        setModalInfo({
          title,
          content,
          showInput,
          maxLength: confirm?.max_length,
          placeholder: confirm?.placeholder,
        });
      } else {
        confirmBtn(clientY);
      }
      track && track(item.id, "button", item);
    },
    300,
    { leading: true, trailing: false }
  );

  const confirmBtn = (clientY = 0, inputValue?: string) => {
    const {
      url,
      pc_url,
      action_type,
      retrieve_values,
      id,
      extra,
      opentag_value,
      success_toast,
      error_toast,
      confirm,
    } = btnItemInfo.current || {};
    if (action_type === "forward") {
      window.open(pc_url || url);
    } else if (action_type === "request") {
      btnRequestHandle({
        url: pc_url || url,
        btnId: id,
        retrieve_values,
        extra_elements: confirm?.show_input
          ? [
              {
                id: id || "",
                type: "confirm",
                value: inputValue || "",
              },
            ]
          : undefined,
        extra,
        success_toast,
        error_toast,
      });
    } else {
      btnExtraHandle && btnExtraHandle({ action_type, clientY, opentag_value });
    }
  };

  const btnWithIcon = (position: number, icon_url?: string) => {
    if (!icon_url) return null;
    return (
      <img
        src={icon_url}
        className={position === 0 ? "leftIcon" : "rightIcon"}
      />
    );
  };

  const modalOkHandle = () => {
    const btnId = btnItemInfo.current?.confirm?.confirmButton?.id;
    track && btnId && track(btnId, "button");

    setConfirmModal(false);
    confirmBtn(0, confirmInputValue || undefined);
    setConfirmInputValue("");
  };

  const { cancelButton, confirmButton } = btnItemInfo.current?.confirm || {};

  // 优化
  const getBtnStyle = (info) => {
    const { text_color, border_color, background_color } = info;
    let style: any = {};
    text_color && (style.color = `#${text_color}`);
    border_color && (style.borderColor = `#${border_color}`);
    background_color && (style.backgroundColor = `#${background_color}`);
    return style;
  };

  const limitConfirmInputValue = (value: string) => {
    const { maxLength } = modalInfo;
    if (!maxLength || value.length <= maxLength) return value;
    return value.slice(0, maxLength);
  };

  const handleConfirmInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = e.target.value;
    setConfirmInputValue(
      isConfirmInputComposingRef.current
        ? nextValue
        : limitConfirmInputValue(nextValue)
    );
  };

  const handleConfirmInputCompositionStart = () => {
    isConfirmInputComposingRef.current = true;
  };

  const handleConfirmInputCompositionEnd = (
    e: React.CompositionEvent<HTMLTextAreaElement>
  ) => {
    isConfirmInputComposingRef.current = false;
    setConfirmInputValue(limitConfirmInputValue(e.currentTarget.value));
  };

  const modalCancelHandle = () => {
    const btnId = btnItemInfo.current?.confirm?.cancelButton?.id;
    track && btnId && track(btnId, "button");

    setConfirmModal(false);
    setConfirmInputValue("");
  };

  return (
    <div style={style}>
      <div
        className={`${"sscard-flexBox"} ${"sscard-btnBox"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {Array.isArray(content) &&
          content.map((item: IMultiElementCombineMsg.IBtnItem) => {
            const { id, disable, text, icon_position = 0, icon_url } = item;
            return (
              <span
                style={btnStyle(item)}
                className={isStreaming || disable ? "sscard-disabled" : ""}
                key={id}
                onClick={(e) => btnClick(e, item)}
              >
                {icon_position === 0 && btnWithIcon(icon_position, icon_url)}
                {getText(text)}
                {icon_position === 1 && btnWithIcon(icon_position, icon_url)}
              </span>
            );
          })}
      </div>
      <CommonModal
        modalTile={modalInfo.title}
        modalVisible={confirmModal}
        closable={true}
        setOKModal={modalOkHandle}
        setonCancelModal={modalCancelHandle}
        modalContent={modalInfo.content}
        getContainer={() =>
          parentBoxName ? document.getElementById(parentBoxName) : document.body
        }
        footer={
          cancelButton?.text || confirmButton?.text
            ? [
                cancelButton?.text && (
                  <Button
                    key="btn1"
                    onClick={modalCancelHandle}
                    style={getBtnStyle(cancelButton)}
                  >
                    {getText(cancelButton?.text)}
                  </Button>
                ),
                confirmButton?.text && (
                  <Button
                    key="btn2"
                    onClick={modalOkHandle}
                    style={getBtnStyle(confirmButton)}
                  >
                    {getText(confirmButton?.text)}
                  </Button>
                ),
              ]
            : undefined
        }
      >
        {modalInfo.showInput && (
          <TextArea
            className="sscard-confirm-input"
            value={confirmInputValue}
            placeholder={
              modalInfo.placeholder ? getText(modalInfo.placeholder) : undefined
            }
            allowClear
            onChange={handleConfirmInputChange}
            onCompositionStart={handleConfirmInputCompositionStart}
            onCompositionEnd={handleConfirmInputCompositionEnd}
            autoSize={{ minRows: 2, maxRows: 2 }}
          />
        )}
      </CommonModal>
    </div>
  );
};
